"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="90786" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2 lg:gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 relative after:absolute after:bottom-2 after:left-4 after:right-4 after:h-[2px] after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-lg md:hidden flex flex-col py-4 px-4 gap-2 animate-in slide-in-from-top-2 duration-300">
      {loading ? <div className="flex flex-col gap-2">
          {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
        </div> : menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="flex items-center h-12 px-4 rounded-md hover:bg-accent hover:text-accent-foreground text-base font-medium transition-colors" onClick={() => setIsOpen(false)}>
            {item.label}
          </Link>)}
      <div className="mt-4 pt-4 border-t border-border sm:hidden">
        <Button variant="default" className="w-full rounded-full h-12 text-base bg-accent hover:bg-accent/90 text-accent-foreground shadow-md" asChild>
          <Link to="#contact" onClick={() => setIsOpen(false)}>
            Kontakt
          </Link>
        </Button>
      </div>
    </div>;
}
export default function Hovedmeny() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="navigering" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out text-foreground", isScrolled || isMobileMenuOpen ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609312/img/image_115nnrsh.png)] border-b border-transparent")}>
      <MenuProvider menu_id="58" className="relative">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609312/img/vandra_enkelt_logo.png" alt="Vandra Enkelt" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="flex-1 flex justify-center px-4">
            <DesktopMenu />
          </div>

          {}
          <div className="flex-shrink-0 flex items-center gap-2 sm:gap-4">
            <Button variant="default" className="hidden sm:inline-flex rounded-full px-6 bg-accent hover:bg-accent/90 text-accent-foreground shadow-md hover:shadow-lg transition-all" asChild>
              <Link to="#contact">Kontakt</Link>
            </Button>
            
            <div className="flex items-center justify-center h-10 w-10 rounded-full hover:bg-accent transition-colors">
              <CartIcon variant="ghost" size="icon" className="text-foreground" />
            </div>

            <Button variant="ghost" size="icon" className="md:hidden rounded-full hover:bg-accent transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6 text-foreground" /> : <Menu className="size-6 text-foreground" />}
            </Button>
          </div>
        </div>

        {}
        <MobileMenu isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </header>;
}